import type { IrrigationPartKind } from '@lib/enums/irrigation-part-kind.enum'
import type { ProposalWork } from '@lib/enums/proposal-work.enum'
import type { ListClientsQuery } from '@lib/input/client.input'
import type { ListDocumentsQuery } from '@lib/input/document.input'
import type { ListProposalsQuery } from '@lib/input/proposal.input'
import type { VideoOwner } from '@lib/input/tree-video.input'

/**
 * Filter fields a list cache key may carry.
 *
 * The keys and the value unions are the query's own, so a misspelled filter
 * or a status that is not one of the real ones cannot enter the key. Explicit
 * `undefined` is allowed because a filter left unset is passed through as
 * `undefined`, and `exactOptionalPropertyTypes` would otherwise reject that.
 */
type ListKeyFilters<Query> = {
  [Key in keyof Query]?: Query[Key] | undefined
}

/**
 * TanStack Query key factories. Centralizing keys here keeps cache reads and
 * invalidations consistent — a mutation invalidates `inviteKeys.lists()` and
 * every list query rooted at it is refreshed, with no stringly-typed drift.
 */
export const inviteKeys = {
  all: ['invites'] as const,
  lists: () => [...inviteKeys.all, 'list'] as const,
  list: () => [...inviteKeys.lists()] as const,
  /**
   * A public token preview. The token is part of the key because each token
   * resolves to a different invite — but note these entries live in the cache
   * of an unauthenticated visitor's tab, so nothing sensitive beyond the
   * invited email is ever stored against them.
   */
  previews: () => [...inviteKeys.all, 'preview'] as const,
  preview: (token: string) => [...inviteKeys.previews(), token] as const,
}

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: () => [...userKeys.lists()] as const,
}

export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  /**
   * Filters are part of the key so each filter combination caches separately.
   * Invalidating `lists()` clears every one of them at once, which is what
   * mutations want — a new client may or may not match the active filter, and
   * guessing is how stale lists happen.
   */
  list: (filters: ListKeyFilters<ListClientsQuery>) =>
    [...clientKeys.lists(), filters] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
}

export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters: ListKeyFilters<ListDocumentsQuery>) =>
    [...documentKeys.lists(), filters] as const,
  /**
   * A pre-signed view link, keyed per document.
   *
   * Cached separately from the document itself because it EXPIRES — the row is
   * stable, the link is not, and giving them one key would tie the lifetime of
   * a listing to the lifetime of a signature.
   */
  downloads: () => [...documentKeys.all, 'download'] as const,
  download: (id: string) => [...documentKeys.downloads(), id] as const,
}

export const proposalKeys = {
  all: ['proposals'] as const,
  lists: () => [...proposalKeys.all, 'list'] as const,
  list: (filters: ListKeyFilters<ListProposalsQuery>) =>
    [...proposalKeys.lists(), filters] as const,
  details: () => [...proposalKeys.all, 'detail'] as const,
  detail: (id: string) => [...proposalKeys.details(), id] as const,
  documents: () => [...proposalKeys.all, 'document'] as const,
  document: (versionId: string) =>
    [...proposalKeys.documents(), versionId] as const,
  meetingRequests: (versionId: string) =>
    [...proposalKeys.all, 'meeting-requests', versionId] as const,
  activity: (versionId: string) =>
    [...proposalKeys.all, 'activity', versionId] as const,
  /** The Housecall Pro jobs an accepted version becomes. */
  hcpJobs: (versionId: string) =>
    [...proposalKeys.all, 'hcp-jobs', versionId] as const,
  followUps: () => [...proposalKeys.all, 'follow-ups'] as const,
  /** Seasons of accepted work coming up for a rebid. */
  seasonReminders: () => [...proposalKeys.all, 'season-reminders'] as const,
  /** A sent proposal as the PDF printer opens it, on a render grant. */
  print: (grant: string) => [...proposalKeys.all, 'print', grant] as const,
  /** A video's playback link on a client's shared proposal. */
  sharedVideo: (token: string, videoId: string) =>
    [...proposalKeys.all, 'shared-video', token, videoId] as const,
}

export const organizationKeys = {
  all: ['organization'] as const,
  /** No id: there is one organization, so there is one entry. */
  detail: () => [...organizationKeys.all, 'detail'] as const,
  /** The public name-and-logo pair, cached separately from the settings. */
  branding: () => [...organizationKeys.all, 'branding'] as const,
}

/**
 * The Housecall Pro connection and the part pickers its copy feeds. Saving or
 * syncing invalidates the whole root, because a new copy changes every list.
 */
export const housecallProKeys = {
  all: ['housecall-pro'] as const,
  status: () => [...housecallProKeys.all, 'status'] as const,
  topCategories: () => [...housecallProKeys.all, 'top-categories'] as const,
  services: () => [...housecallProKeys.all, 'services'] as const,
  parts: (kind: IrrigationPartKind | null) =>
    [...housecallProKeys.all, 'parts', kind] as const,
  /** A search of HCP's customers, read live. */
  customerSearch: (query: string) =>
    [...housecallProKeys.all, 'customers', 'search', query] as const,
  /** One HCP customer with its addresses, read live. */
  customer: (id: string) => [...housecallProKeys.all, 'customers', id] as const,
}

/** A site's irrigation system: read whole, so one entry per site. */
export const irrigationKeys = {
  all: ['irrigation'] as const,
  /** A site's system, every visit's. Invalidating this reaches them all. */
  sites: (siteId: string) => [...irrigationKeys.all, 'site', siteId] as const,
  /** A site's system with one visit's problems; null is the current visit. */
  site: (siteId: string, visitId: string | null) =>
    [...irrigationKeys.sites(siteId), visitId ?? 'current'] as const,
  /**
   * The repair estimate, keyed by the site's repairs as the server last had
   * them, so it is asked again whenever a sync changes them.
   */
  estimate: (siteId: string, revision: string) =>
    [...irrigationKeys.all, 'estimate', siteId, revision] as const,
}

/** A site's visits. */
export const siteVisitKeys = {
  all: ['site-visits'] as const,
  list: (siteId: string) => [...siteVisitKeys.all, siteId] as const,
  /** Under the list, so whatever refreshes a site's visits refreshes its history. */
  history: (siteId: string) =>
    [...siteVisitKeys.list(siteId), 'history'] as const,
  /** One record's own history, under its site's. */
  record: (siteId: string, recordId: string) =>
    [...siteVisitKeys.history(siteId), 'record', recordId] as const,
  /** A site as it stood on one visit's day. */
  map: (siteId: string, visitId: string) =>
    [...siteVisitKeys.list(siteId), 'map', visitId] as const,
}

export const siteKeys = {
  all: ['sites'] as const,
  details: () => [...siteKeys.all, 'detail'] as const,
  detail: (id: string) => [...siteKeys.details(), id] as const,
}

/** Areas drawn inside one site. */
export const areaKeys = {
  all: ['areas'] as const,
  list: (siteId: string) => [...areaKeys.all, siteId] as const,
}

export const treeKeys = {
  all: ['trees'] as const,
  lists: () => [...treeKeys.all, 'list'] as const,
  /** A site's trees, every visit's. Invalidating this reaches them all. */
  site: (siteId: string) => [...treeKeys.lists(), siteId] as const,
  /**
   * One site's trees with one visit's findings: a survey is read whole, for
   * one place, as one visit saw it. Null is the site's current visit.
   */
  list: (siteId: string, visitId: string | null) =>
    [...treeKeys.site(siteId), visitId ?? 'current'] as const,
}

/**
 * Proposal wording. Two levels, keyed separately: the organization's standard
 * set, and one proposal's changes to it.
 */
export const proposalTemplateKeys = {
  all: ['proposal-template'] as const,
  /** The business's set for one kind of proposal: trees, irrigation, or both. */
  organization: (work: ProposalWork) =>
    [...proposalTemplateKeys.all, 'organization', work] as const,
  proposal: (id: string) =>
    [...proposalTemplateKeys.all, 'proposal', id] as const,
}

/** The irrigation problem directory: one list, so no per-id keys. */
export const irrigationProblemTypeKeys = {
  all: ['irrigation-problem-types'] as const,
  list: () => [...irrigationProblemTypeKeys.all, 'list'] as const,
}

export const treeProblemTypeKeys = {
  all: ['tree-problem-types'] as const,
  list: () => [...treeProblemTypeKeys.all, 'list'] as const,
}

/** The species catalog: one list, so no per-id keys. */
export const treeSpeciesKeys = {
  all: ['tree-species'] as const,
  list: () => [...treeSpeciesKeys.all, 'list'] as const,
}

/**
 * Connected assistants. The consent request is keyed by its id because each
 * link is a different ask; the grant list is one person's own connections.
 */
export const oauthKeys = {
  all: ['oauth'] as const,
  requests: () => [...oauthKeys.all, 'request'] as const,
  request: (id: string) => [...oauthKeys.requests(), id] as const,
  grants: () => [...oauthKeys.all, 'grants'] as const,
}

/** Videos of a tree, plus whether the deployment supports them at all. */
export const treeVideoKeys = {
  all: ['tree-videos'] as const,
  availability: () => [...treeVideoKeys.all, 'availability'] as const,
  lists: () => [...treeVideoKeys.all, 'list'] as const,
  /** One owner's videos, every visit's: `{ treeId }`, or one irrigation record. */
  owner: (owner: VideoOwner) => [...treeVideoKeys.lists(), owner] as const,
  /** One owner's videos from one visit; null is every visit's. */
  list: (owner: VideoOwner, visitId: string | null) =>
    [...treeVideoKeys.owner(owner), visitId ?? 'any'] as const,
  /** One video by id — how a stock clip is read, having no tree to list. */
  detail: (id: string) => [...treeVideoKeys.all, 'detail', id] as const,
}
